import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

interface Reservation {
  id: number;
  bookingDate: string;
  startTime: number;
  endTime: number;
  user2Id: string;
  completed: boolean;
  startTimeFormatted: string;
  endTimeFormatted: string;
}

@Component({
  selector: 'admin-bookreservation',
  templateUrl: './bookreservation.component.html',
  styleUrls: ['./bookreservation.component.scss']
})
export class AdminBookReservationComponent implements OnInit {
  reservationForm: FormGroup;
  timeSlots: { display: string; value: number }[] = [];
  opponents: { display: string; value: number }[] = [];
  isSubmitting = false;
  submitMessage = '';
  reservations: Reservation[] = [];
  isLoadingReservations = false;
  deleteMessage = '';
  editingReservationId: number | null = null;

  constructor(private fb: FormBuilder, private http: HttpClient) {
    this.reservationForm = this.fb.group({
      date: ['', Validators.required],
      startTime: ['', Validators.required],
      stopTime: ['', Validators.required],
      opponentId: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.initializeTimeSlots();
    this.initializeOpponents();
    this.preselectEric();
    this.loadReservations();
  }

  private initializeTimeSlots(): void {
    // Generate time slots from 8:30 AM to 8:30 PM, 30 minutes apart
    const startHour = 8;
    const startMinute = 30;
    const endHour = 20;
    const endMinute = 30;
    
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === startHour && minute < startMinute) continue;
        if (hour === endHour && minute > endMinute) continue;
        
        const timeString = this.formatTime(hour, minute);
        const secondsSinceMidnight = (hour * 3600) + (minute * 60);
        
        this.timeSlots.push({
          display: timeString,
          value: secondsSinceMidnight
        });
      }
    }
  }

  private initializeOpponents(): void {
    this.opponents = [
      { display: 'Paul Adams', value: 930090 },
      { display: 'David Altman', value: 337946 },
      { display: 'Ricardo Barrozzi', value: 15523 },
      { display: 'Marc Blum', value: 1930638 },
      { display: 'Constantine Bohorodita', value: 143568 },
      { display: 'Mike Cianci', value: 14877 },
      { display: 'Josh Davis', value: 16439 },
      { display: 'Martin Devincenti', value: 1675354 },
      { display: 'Jacques Elmaleh', value: 14648 },
      { display: 'Jeff Grubman', value: 15004 }, 
      { display: 'Nick Islas', value: 2665556 },
      { display: 'Michael Jordan', value: 82174 },
      { display: 'Daniel Lebowitz', value: 730364 },
      { display: 'Christian Manzano', value: 377121 },
      { display: 'Tom Micek', value: 1381396 },
      { display: 'Clay Moran', value: 14428 },
      { display: 'Jeremy Olsher', value: 52135 },
      { display: 'Daniel Parent', value: 905579 },
      { display: 'Eric Placeholder', value: 0 },  // 27186
      { display: 'Sam Reznitsky', value: 69959 },
      { display: 'David Siegel', value: 673077 },
      { display: 'Elliot Warman', value: 1231403 },
      //{ display: '', value: },
    ];
    this.opponents = this.opponents.sort((a, b) => {
      const lastNameA = a.display.split(' ').pop() || '';
      const lastNameB = b.display.split(' ').pop() || '';
      return lastNameA.localeCompare(lastNameB);
    });
  }

  private preselectEric(): void {
    // Preselect Eric Placeholder
    this.reservationForm.patchValue({
      opponentId: 0   // 27186
    });
  }

  private formatTime(hour: number, minute: number): string {
    const period = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const displayMinute = minute === 0 ? '00' : minute.toString();
    return `${displayHour}:${displayMinute}${period}`;
  }

  setMorningTime(): void {
    // 8:30 AM = 30600 seconds, 10:00 AM = 36000 seconds
    this.reservationForm.patchValue({
      startTime: 30600,
      stopTime: 36000
    });
  }

  setEveningTime(): void {
    // 6:30 PM = 66600 seconds, 8:00 PM = 72000 seconds
    this.reservationForm.patchValue({
      startTime: 66600,
      stopTime: 72000
    });
  }

  async onSubmit(): Promise<void> {
    if (this.reservationForm.valid) {
      this.isSubmitting = true;
      this.submitMessage = '';

      try {
        const formData = this.reservationForm.value;
        
        if (this.editingReservationId) {
          // Update existing reservation
          const response: any = await this.http.put(`/admin/updatereservation/${this.editingReservationId}`, {
            bookingDate: formData.date,
            startTime: formData.startTime,
            stopTime: formData.stopTime,
            opponentId: formData.opponentId > 0 ? formData.opponentId : null
          }).toPromise();

          if (response.success) {
            this.submitMessage = `Reservation updated successfully!`;
            this.cancelEdit();
            this.loadReservations(); // Refresh the reservations list
          } else {
            this.submitMessage = 'Error: ' + (response.message || 'Unknown error occurred');
          }
        } else {
          // Create new reservation
          const response: any = await this.http.post('/admin/bookreservationapi', {
            bookingDate: formData.date,
            startTime: formData.startTime,
            stopTime: formData.stopTime,
            opponentId: formData.opponentId > 0 ? formData.opponentId : null
          }).toPromise();

          if (response.success) {
            this.submitMessage = `Reservation booked successfully! Booking ID: ${response.bookingId}`;
            this.reservationForm.reset();
            this.preselectEric(); // Reselect Eric after reset
            this.loadReservations(); // Refresh the reservations list
          } else {
            this.submitMessage = 'Error: ' + (response.message || 'Unknown error occurred');
          }
        }
      } catch (error: any) {
        console.error('Error booking reservation:', error);
        this.submitMessage = 'Error: ' + (error.error || 'Failed to book reservation');
      } finally {
        this.isSubmitting = false;
      }
    }
  }

  async loadReservations(): Promise<void> {
    this.isLoadingReservations = true;
    try {
      const response: any = await this.http.get('/admin/getreservations').toPromise();
      this.reservations = response || [];
    } catch (error: any) {
      console.error('Error loading reservations:', error);
      this.reservations = [];
    } finally {
      this.isLoadingReservations = false;
    }
  }

  async deleteReservation(id: number): Promise<void> {
    if (!confirm('Are you sure you want to delete this reservation?')) {
      return;
    }

    this.deleteMessage = '';
    try {
      const response: any = await this.http.delete(`/admin/deletereservation/${id}`).toPromise();
      
      if (response.success) {
        this.deleteMessage = 'Reservation deleted successfully';
        this.loadReservations(); // Refresh the list
      } else {
        this.deleteMessage = 'Error: ' + (response.message || 'Failed to delete reservation');
      }
    } catch (error: any) {
      console.error('Error deleting reservation:', error);
      this.deleteMessage = 'Error: ' + (error.error || 'Failed to delete reservation');
    }
  }

  getOpponentName(user2Id: string): string {
    if (!user2Id || user2Id === '0') {
      return 'Eric Placeholder';
    }
    
    const opponent = this.opponents.find(o => o.value.toString() === user2Id);
    return opponent ? opponent.display : `User ID: ${user2Id}`;
  }

  formatDate(dateString: string): string {
    // Parse the date string as local time to avoid timezone conversion issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  editReservation(reservation: Reservation): void {
    // Set the editing ID
    this.editingReservationId = reservation.id;
    
    // Populate the form with the reservation data
    this.reservationForm.patchValue({
      date: reservation.bookingDate,
      startTime: reservation.startTime,
      stopTime: reservation.endTime,
      opponentId: reservation.user2Id || 0
    });
    
    // Clear any previous messages
    this.submitMessage = '';
    this.deleteMessage = '';
    
    // Scroll to the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  cancelEdit(): void {
    this.editingReservationId = null;
    this.reservationForm.reset();
    this.preselectEric();
    this.submitMessage = '';
  }
}
